export type HealthResponse = {
  status: 'ok';
  service: string;
  version: string;
  timestamp: string;
};

export type LogSource = { id: string; label: string; available: boolean };
export type LogEntry = { timestamp: string; level: string; message: string; trace: string };
export type LogsResponse = {
  source: string;
  label: string;
  lines: string[];
  entries: LogEntry[];
  updated_at: string | null;
  available: boolean;
  sources: LogSource[];
  sender_options: Array<{ id: number; name: string }>;
};

export type DatabaseRefreshExcludedTable = { table: string; size_mb: number; included?: boolean };
export type DatabaseRefreshTable = DatabaseRefreshExcludedTable & { ignored: boolean };
export type DatabaseRefreshDatabase = {
  source: string;
  target: string;
  shared?: boolean;
  excluded_tables: DatabaseRefreshExcludedTable[];
  tables?: DatabaseRefreshTable[];
};
export type DatabaseRefreshStatus = {
  enabled: boolean;
  state: 'idle' | 'running' | 'succeeded' | 'failed';
  message: string;
  started_at: string | null;
  finished_at: string | null;
  threshold_mb: number;
  confirmation: string;
  databases: DatabaseRefreshDatabase[];
};

export type AuthUser = {
  id: number | string;
  username: string;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
  first_name: string | null;
  last_name: string | null;
};

export type Authorization = {
  direct_roles: string[];
  effective_roles: string[];
  capabilities: string[];
};

export type AuthContext = {
  user: AuthUser;
  authorization: Authorization;
};

export type ForgotPasswordResponse = { status: 'email_sent' | 'admin_contact' };
export type ResetPasswordResponse = { status: 'password_reset' };

export type UpdateOwnProfile = {
  current_password?: string;
  password?: string;
  password_confirmation?: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
};

export type UpdateOwnProfileResponse = AuthContext & {
  status: 'updated' | 'email_verification_required';
  masked_email?: string | null;
};

export type ManagedRoleSummary = { id: number; name: string };

export type ManagedUser = {
  id: number;
  username: string;
  language: 'cs' | 'en' | 'de';
  created_at: string;
  parent_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  roles: ManagedRoleSummary[];
  can_edit: boolean;
  can_manage_access: boolean;
  can_delete: boolean;
};

export type ManagedUsersResponse = {
  users: ManagedUser[];
  role_options: ManagedRoleSummary[];
  can_change_parent: boolean;
};

export type ManagedUserDeletionAction = 'delete' | 'anonymize' | 'reassign';
export type ManagedUserDeletionImpact = {
  user: { id: number; username: string };
  items: Array<{ key: string; count: number; action: ManagedUserDeletionAction }>;
  total_records: number;
  can_delete: boolean;
  blocked_reason: string | null;
};

export type ManagedRole = {
  id: number;
  name: string;
  parent_ids: number[];
  users_count: number;
  direct_capabilities: string[];
  effective_capabilities: string[];
  protected: boolean;
};

export type SaveManagedUser = {
  username: string;
  password?: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
  parent_id: number | null;
  role_ids: number[];
};

export type SaveManagedRole = { name: string; parent_ids: number[] };
export type ManagedUserNeuronAccess = { id: number; name: string; selected: boolean };
export type ManagedUserCameraAccess = { id: number; name: string; selected: boolean };
export type ManagedUserAccess = { neurons: ManagedUserNeuronAccess[]; camera_access_available: boolean; camera_ids: number[] };
export type NotificationPermission = 0 | 1 | 2;
export type ManagedUserNotificationAccess = { id: number; name: string; permission: NotificationPermission };
export type ManagedAlertType = { id: number; name: string; description: string };
export type ManagedAlertDefinition = {
  id: number;
  name: string;
  description?: string | null;
  csid: number;
  roi_id?: number | null;
  type_id: number;
  sender_id?: number | null;
  event_list_name?: string | null;
  activated?: boolean | number;
  send_to_sender?: boolean | number;
  default_sender_value?: boolean | number;
  force_sender_value?: number | null;
  gpio_bit?: number | null;
  client_server_name?: string | null;
  client_server_label?: string | null;
  type_name?: string | null;
  sender_name?: string | null;
  access_level: 'full' | 'limited';
  can_edit: boolean;
};
export type ManagedOption = { id: number; name: string };
export type ManagedAlertDefinitionResponse = {
  data: ManagedAlertDefinition[];
  options: { client_servers: ManagedOption[]; alert_types: ManagedOption[]; senders: ManagedOption[] };
  permissions: { can_create: boolean; can_manage_global: boolean };
};
export type ManagedAlertCamera = { id: number; name: string; room_name?: string | null; room_label?: string | null; width: number; height: number; http_stream?: string | null };
export type ManagedAlertMask = {
  id: number;
  mask_id: number;
  camera_id: number;
  mask_name?: string | null;
  analysis_type: string;
  activated: boolean | number;
  classes: { person: boolean; car: boolean; animal: boolean; light: boolean };
  points: CameraMaskPoint[];
};
export type ManagedAlertMasks = { cameras: ManagedAlertCamera[]; masks: ManagedAlertMask[]; analysis_types: string[] };

export type Installation = {
  id: number;
  name: string;
  ip: string;
};

export type PushDevice = {
  id: number;
  installation_id: string;
  firebase_project: string;
  platform: 'web' | 'ios' | 'android';
  label: string | null;
  locale: 'cs' | 'en' | 'de';
  app_version: string | null;
  enabled: boolean;
  push_capable: boolean;
  last_seen_at: string;
  last_success_at: string | null;
  last_failure_at?: string | null;
  last_failure_code?: string | null;
};

export type RegisterPushDevice = {
  installation_id: string;
  fcm_token: string;
  platform: PushDevice['platform'];
  label?: string | null;
  locale?: PushDevice['locale'];
  app_version?: string | null;
  enabled?: boolean;
  push_capable?: boolean;
};

export type PushArea = { aid: number; name: string; permission: 'full' | 'limited'; enabled: boolean };

export type NotificationPreferenceArea = {
  aid: number;
  name: string;
  permission: 'full' | 'limited';
  default_sender_value: boolean | null;
  paused: boolean;
  paused_until: string | null;
};

export type ManagedSender = {
  id: number;
  name: string;
  description: string;
  event_address: string;
  maintenance_address: string;
  debug_payload?: boolean | null;
  debug_payload_supported?: boolean;
  protocol?: 'modern' | 'legacy';
  protocol_type?: 'modern' | 'legacy';
  protocol_version?: number | null;
  database?: Record<string, string | number | null>;
  status?: { reachable: boolean; message: string; remote?: unknown };
  debug?: SenderDebugEntry[];
};

export type SenderEventType = {
  id?: number | string;
  type?: string;
  name?: string;
  label?: string;
  description?: string;
  parameters?: string[] | Record<string, { type?: string; required?: boolean }>;
};

export type SenderEventStep = { id?: number | string; type?: string; values: Record<string, string> };
export type SenderEventList = { name: string; steps: SenderEventStep[] };
export type SenderDebugEntry = {
  protocol: 'modern' | 'legacy' | 'discovery';
  method: string;
  url: string;
  command?: string;
  parameters?: Record<string, unknown>;
  status: number;
  response: string;
};

export type NotificationScheduleEntry = { aid: number; day: number; hour: number; active: boolean; mode?: 'none' | 'on' | 'off' | 'custom'; start_minute?: number; end_minute?: number };
export type NotificationPreferences = {
  available: boolean;
  can_manage_advanced: boolean;
  areas: NotificationPreferenceArea[];
  schedule: NotificationScheduleEntry[];
};

export type NeoNotificationMedia = {
  id: number;
  position: number;
  thumbnail_url: string | null;
  original_url: string | null;
  legacy_hash?: string | null;
  downloaded?: boolean;
};

export type NeoNotification = {
  id: number;
  delivery_id: number;
  source?: 'neo' | 'legacy';
  type: string;
  title: string;
  body: string | null;
  aid: number;
  cid: number | null;
  occurred_at: string;
  ended_at?: string | null;
  status: 'queued' | 'sent' | 'failed';
  read_at: string | null;
  reported: boolean;
  media: NeoNotificationMedia[];
};

export type SensorDefinition = { id: number; cid: number; type: string; bit: number | null; bit_state: number; output: number; current_state: number | null; set_cmd: string | null; clear_cmd: string | null };
export type CameraDefinition = { id: number; cid: number; stream_pass?: string | null; [key: string]: string | number | null | undefined };
export type NeuronCamera = { id: number; name: string | null; label?: string | null; user_label?: string | null; description?: string | null; warranty_until?: string | null; type: 'camera' | 'sensor'; sensor?: SensorDefinition | null; definition?: CameraDefinition | null };
export type NeuronRoom = { id: number; csid: number; name: string | null; label?: string | null; user_label?: string | null; description: string | null; created: string | null; updated: string | null; type: 'room'; cameras: NeuronCamera[] };
export type RoomBorderPoint = { id?: number; step?: number; x: number; y: number };
export type CameraMaskPoint = { id?: number; mid?: number; step?: number; pix_x: number; pix_y: number; usable: 0 | 1 | 2 };
export type CameraMask = { id: number; cid: number; type: number; value: number | null; name: string | null; description: string | null; points: CameraMaskPoint[] };
export type CameraPoint = { id?: number; cid?: number; p_type: number; abs_x: number; abs_y: number; abs_z: number; pix_x: number; pix_y: number };
export type NeuronPoeMode = 0 | 1 | 2 | 3;
export type NeuronLocalServer = { id: number; cs_id: number; name: string | null; label?: string | null; user_label?: string | null; description: string | null; ip_address: string | null; res_x: number; res_y: number };
export type NeuronTiming = { id: number; csid?: number; rid?: number; start: string; end: string; weekday: number };
export type SaveNeuronTiming = { weekday: number; start: string; end: string };
export type NeuronLayoutCamera = { id: number; name: string | null; room_name: string | null };
export type NeuronCameraLayoutItem = { id: number; ls_id: number; cid: number; pos_left: number; pos_top: number; width: number; height: number };
export type SaveNeuronCameraLayoutItem = Omit<NeuronCameraLayoutItem, 'id' | 'ls_id'>;
export type NeuronCameraLayout = { canvas: { width: number; height: number }; cameras: NeuronLayoutCamera[]; layout: NeuronCameraLayoutItem[] };
export type NeuronVirtualCameraItem = { id: number; master_cid: number; servant_cid: number; servant_name: string | null; left: number; top: number; width: number; height: number; weight: number };
export type NeuronVirtualCamera = { canvas: { width: number; height: number }; servants: NeuronLayoutCamera[]; layout: NeuronVirtualCameraItem[] };
export type CameraRecordSettings = { res_x: number; fps: number; quality: number; codec: string };
export type CameraRecords = { hours: Record<number, boolean[]>; record: CameraRecordSettings; stream: CameraRecordSettings };
export type SaveCameraRecords = { hours: Record<number, boolean[]>; record: CameraRecordSettings; stream: CameraRecordSettings };
export type CameraStreamingSystem = 'legacy' | 'neo';
export type SurveillanceCamera = {
  id: number;
  name: string;
  label: string | null;
  system_name: string | null;
  user_label: string | null;
  description: string | null;
  room: { id: number; name: string; system_label?: string | null; system_name?: string | null; user_label?: string | null };
  neuron: { id: number; name: string; system_label?: string | null; system_name?: string | null; user_label?: string | null };
  streaming_system: CameraStreamingSystem;
  live_available: boolean;
  records_available: boolean;
  favorite: boolean;
  favorite_weight: number | null;
  thumbnail_url: string;
};
export type UpdateSurveillanceCameraLabelResponse = { status: string; scope: 'global' | 'user'; name: string; label: string | null; user_label: string | null };
export type CameraRecordInterval = { from: string; till: string | null; state?: 'NORMAL' | 'MOTION' };
export type NeoCodec = 'H264' | 'H265';
export type NeoTimeline = { cid: string; from_ts: string; to_ts: string; generated_at_ts: string; intervals: Array<{ start_ts: string; end_ts: string; state: 'GAP' | 'NORMAL' | 'MOTION' }> };
export type NeoPlaybackSession = { playback_id: string; cid: string; session_type: 'LIVE_EVENT' | 'HISTORICAL_RANGE'; hls_path: string; keepalive_interval_seconds: string; lease_timeout_seconds: string; supported_codecs: NeoCodec[]; timeline: NeoTimeline; initial_history_complete?: boolean };
export type CameraRecordEvent = { at: string; type: string; count: number };
export type NeoCameraTimeline = NeoTimeline & { alerts: CameraRecordEvent[] };
export type CameraRecordTimeline = { intervals: CameraRecordInterval[]; alerts: CameraRecordEvent[] };
export type CameraPlaylist = { url: string; file_sizes?: unknown; segments?: Array<{ start: number | string; length: number | string; url?: string }>; [key: string]: unknown };
export type NeuronServer = {
  id: number; required_fields?: string[]; name: string | null; label?: string | null; system_label?: string | null; system_name?: string | null; user_label?: string | null; description: string | null; documentation?: string | null;
  warranty_until?: string | null;
  vpn_ip_addr?: string | null; ip_addr?: string | null; vpn_ip?: string | null; modem_type?: string | null; type: string | null;
  dongle_type?: string | null; ups_type?: string | null; nas_user?: string | null; rsync_bitrate?: number | null;
  nas_server?: string | null; nas_folder?: string | null; stream_server?: string | null; stream_user?: string | null;
  streaming_system?: CameraStreamingSystem;
  local_monitor_enable?: number; num_days_record?: number | null; poe_mode: NeuronPoeMode; apply_settings: number;
  local_servers: NeuronLocalServer[]; rooms: NeuronRoom[];
};
export type UpdateNeuronLabelResponse = { status: string; scope: 'global' | 'user'; name: string | null; label: string | null; user_label: string | null };

export type LicensePlateList = {
  id: number; name: string; description: string | null; max_plates: number;
  plate_count: number; neuron_ids: number[]; neurons: Array<{ id: number; name: string }>;
};
export type LicensePlateListsResponse = { data: LicensePlateList[]; neurons: Array<{ id: number; name: string }> };
export type LicensePlateCar = { id: number; number: string; car_description: string | null; note: string | null };
export type LicensePlateDefinition = { id: number; cid: number; sensor_id: number; mid: number; camera: NeuronCamera; neuron: { id: number; name: string } };
export type LicensePlateTreeSensor = { id: number; name: string | null; label?: string | null };
export type LicensePlateTreeCamera = { id: number; name: string | null; label?: string | null };
export type LicensePlateTreeRoom = { id: number; name: string | null; label?: string | null; cameras: LicensePlateTreeCamera[]; sensors: LicensePlateTreeSensor[] };
export type LicensePlateTreeNeuron = { id: number; name: string; rooms: LicensePlateTreeRoom[] };
export type LicensePlateDefinitionsResponse = { data: LicensePlateDefinition[]; cameras: NeuronCamera[]; tree: LicensePlateTreeNeuron[] };

export type ImageData = {
  id: number;
  cid: number;
  validation_class: number;
  date: string;
  image?: string | null;
  frames?: AlertFrame[];
};

export type AlertFrame = {
  id: number;
  class_id: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AlertTrainingPayload = {
  image_id: number;
  action: 'save' | 'negative' | 'unknown';
  class_id: number;
  areas?: Array<{
    class_id: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
};

export type SuperCheckGroup = {
  class_id: number;
  count: number;
};

export type UserReportedError = {
  id: number;
  report_key: string;
  source: 'legacy' | 'neo';
  report_type: 'legacy_alert_error' | 'notification_issue' | 'missing_notification';
  category: UserReportCategory | null;
  installation: string | null;
  occurred_at: string | null;
  ended_at: string | null;
  error_description: string;
  resolved: boolean;
  resolution_status?: 'resolved' | 'not_problem' | null;
  resolution_note?: string | null;
  reporter_email: string | null;
  aid?: number | null;
  cid?: number | null;
  notification_id?: number | null;
};

export type UserReportCategory = 'false_alarm' | 'wrong_image' | 'incorrect_detection' | 'duplicate_notification' | 'missing_notification' | 'other';
export type CreateUserReport = {
  type: 'notification_issue' | 'missing_notification';
  category: UserReportCategory;
  description: string;
  notification_id?: number;
  aid?: number;
  cid?: number;
  occurred_at?: string;
};
export type MyUserReport = {
  id: number;
  type: 'notification_issue' | 'missing_notification';
  category: UserReportCategory;
  installation: string | null;
  occurred_at: string | null;
  description: string;
  notification_id: number | null;
  resolved: boolean;
  resolution_status?: 'resolved' | 'not_problem' | null;
  resolution_note?: string | null;
  created_at: string | null;
};
export type UserReportDetail = UserReportedError & {
  context: Record<string, unknown> | null;
  media: Array<{ id: number; thumbnail_url: string; original_url: string }>;
};

export type VerificationRequiredResponse = {
  status: 'verification_required';
  masked_email: string;
  expires_in: number;
};

export type EmailSetupRequiredResponse = {
  status: 'email_setup_required';
  expires_in: number;
};

export type LoginResponse =
  | VerificationRequiredResponse
  | EmailSetupRequiredResponse;

export type VerifyResponse = {
  status: 'authenticated';
  user: AuthUser;
  token: string;
  details?: AuthTokenDetails;
};

export type TokenDays = 1 | 7 | 30 | 90 | 365;
export type AuthTokenDetails = { created_at: string; expires_at: string; ttl_days: number };
export type AuthTokenResponse = { token: string | null; details?: AuthTokenDetails | null };

export type AnalyticsScope = 'public' | 'private' | 'audit';
export type AnalyticsDashboard = {
  scope: AnalyticsScope;
  days: number;
  generated_at: string;
  metrics: Array<{ key: string; value: number }>;
  daily: Array<{ date: string; value: number }>;
  top: Array<{ label: string; value: number }>;
  recent: Array<Record<string, string | number | null>>;
  breakdowns?: Record<string, Array<{ label: string; value: number }>>;
};

export type PublicAnalyticsEvent = {
  event_uuid: string;
  event_name: 'page_view' | 'consent_granted' | 'cta_click' | 'form_open' | 'form_submit' | 'section_view' | 'scroll_depth' | 'engagement';
  consent_level: 'minimal' | 'full';
  visitor_uuid?: string | null;
  session_uuid?: string | null;
  path: string;
  referrer_host?: string | null;
  locale?: string | null;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  viewport_width?: number;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  section_name?: string | null;
  duration_ms?: number | null;
  scroll_depth?: 25 | 50 | 75 | 90 | 100 | null;
  metadata?: Record<string, string | number | boolean | null>;
};

export type ProductAnalyticsEvent = {
  event_uuid: string;
  session_uuid: string;
  event_name: 'page_view' | 'feature_use';
  route: string;
  platform?: 'web' | 'ios' | 'android';
  app_version?: string;
  metadata?: Record<string, string | number | boolean | null>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: Record<string, string[]> = {},
    public readonly code?: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let apiAuthToken: string | null = null;

export function setApiAuthToken(token: string | null): void {
  apiAuthToken = token;
}

export function getApiAuthHeaders(): Record<string, string> {
  return apiAuthToken ? { Authorization: `Bearer ${apiAuthToken}` } : {};
}

function authHeaders(): Record<string, string> {
  return getApiAuthHeaders();
}

function apiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}/api/v1${path}`;
}

function readXsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith('XSRF-TOKEN='));

  return cookie ? decodeURIComponent(cookie.slice('XSRF-TOKEN='.length)) : null;
}

async function parseApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    errors?: Record<string, string[]>;
    code?: string;
    retry_after_seconds?: number;
  };

  return new ApiError(
    body.message ?? `HTTP ${response.status}`,
    response.status,
    body.errors,
    body.code,
    body.retry_after_seconds,
  );
}

async function csrfHeaders(
  baseUrl: string,
  locale: string,
): Promise<Record<string, string>> {
  const response = await fetch(apiUrl(baseUrl, '/auth/csrf'), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': locale,
      ...authHeaders(),
    },
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const body = (await response.json().catch(() => ({}))) as { token?: string };
  if (body.token) {
    return { 'X-CSRF-TOKEN': body.token };
  }

  const cookieToken = readXsrfToken();
  return cookieToken ? { 'X-XSRF-TOKEN': cookieToken } : {};
}

async function mutateJson<T>(
  baseUrl: string,
  path: string,
  body?: unknown,
  locale = 'cs',
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
): Promise<T> {
  const xsrfHeaders = await csrfHeaders(baseUrl, locale);
  const response = await fetch(apiUrl(baseUrl, path), {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': locale,
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...xsrfHeaders,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function getJson<T>(baseUrl: string, path: string): Promise<T> {
  const response = await fetch(apiUrl(baseUrl, path), {
    credentials: 'include',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
  return (await response.json()) as T;
}

export async function getHealth(
  baseUrl: string,
  signal?: AbortSignal,
): Promise<HealthResponse> {
  const response = await fetch(apiUrl(baseUrl, '/health'), {
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as HealthResponse;
}

export function getLogs(baseUrl: string, source = 'laravel', lines = 300, senderId?: number): Promise<LogsResponse> {
  const senderQuery = senderId === undefined ? '' : `&sender_id=${senderId}`;
  return getJson<LogsResponse>(baseUrl, `/logs?source=${encodeURIComponent(source)}&lines=${lines}${senderQuery}`);
}

export async function trackPublicAnalyticsEvent(baseUrl: string, event: PublicAnalyticsEvent): Promise<void> {
  const response = await fetch(apiUrl(baseUrl, '/analytics/public'), {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
  });
  if (!response.ok) throw await parseApiError(response);
}

export async function trackProductAnalyticsEvent(baseUrl: string, event: ProductAnalyticsEvent, locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, '/analytics/events', event, locale);
}

export function getAnalyticsDashboard(baseUrl: string, scope: AnalyticsScope, days: 7 | 30 | 90 | 395): Promise<AnalyticsDashboard> {
  return getJson<AnalyticsDashboard>(baseUrl, `/admin/analytics?scope=${scope}&days=${days}`);
}

export function getDatabaseRefreshStatus(baseUrl: string): Promise<DatabaseRefreshStatus> {
  return getJson<DatabaseRefreshStatus>(baseUrl, '/settings/databases');
}

export function refreshTestDatabases(baseUrl: string, confirmation: string, includedTables: Array<{ source: string; table: string }> = [], locale = 'cs', databases?: string[], onlyTables: Array<{ source: string; table: string }> = []): Promise<DatabaseRefreshStatus> {
  return mutateJson<DatabaseRefreshStatus>(baseUrl, '/settings/databases/refresh', { confirmation, included_tables: includedTables, ...(databases ? { databases } : {}), ...(onlyTables.length ? { only_tables: onlyTables } : {}) }, locale);
}

export function getAuthContext(baseUrl: string): Promise<AuthContext> {
  return getJson<AuthContext>(baseUrl, '/auth/me');
}

export function updateOwnProfile(baseUrl: string, data: UpdateOwnProfile, locale = 'cs'): Promise<UpdateOwnProfileResponse> {
  return mutateJson<UpdateOwnProfileResponse>(baseUrl, '/auth/me', data, locale, 'PUT');
}

export function login(
  baseUrl: string,
  credentials: { login: string; password: string },
  locale = 'cs',
): Promise<LoginResponse> {
  return mutateJson<LoginResponse>(baseUrl, '/auth/login', credentials, locale);
}

export function forgotPassword(baseUrl: string, login: string, locale = 'cs'): Promise<ForgotPasswordResponse> {
  return mutateJson<ForgotPasswordResponse>(baseUrl, '/auth/password/forgot', { login }, locale);
}

export function resetPassword(baseUrl: string, token: string, email: string, password: string, passwordConfirmation: string, locale = 'cs'): Promise<ResetPasswordResponse> {
  return mutateJson<ResetPasswordResponse>(baseUrl, '/auth/password/reset', { token, email, password, password_confirmation: passwordConfirmation }, locale);
}

export function setupEmail(
  baseUrl: string,
  email: string,
  emailConfirmation: string,
  locale = 'cs',
): Promise<VerificationRequiredResponse> {
  return mutateJson<VerificationRequiredResponse>(
    baseUrl,
    '/auth/setup-email',
    { email, email_confirmation: emailConfirmation },
    locale,
  );
}

export function verifyTwoFactor(
  baseUrl: string,
  code: string,
  locale = 'cs',
  tokenDays: TokenDays = 365,
): Promise<VerifyResponse> {
  return mutateJson<VerifyResponse>(baseUrl, '/auth/verify', { code, token_days: tokenDays }, locale);
}

export function getAuthToken(baseUrl: string): Promise<AuthTokenResponse> {
  return getJson<AuthTokenResponse>(baseUrl, '/auth/token');
}

export function issueAuthToken(baseUrl: string, tokenDays: TokenDays, locale = 'cs'): Promise<AuthTokenResponse> {
  return mutateJson<AuthTokenResponse>(baseUrl, '/auth/token', { token_days: tokenDays }, locale);
}

export function renewAuthToken(baseUrl: string, tokenDays: TokenDays, locale = 'cs'): Promise<AuthTokenResponse> {
  return mutateJson<AuthTokenResponse>(baseUrl, '/auth/token', { token_days: tokenDays }, locale, 'PATCH');
}

export function revokeAuthToken(baseUrl: string, locale = 'cs'): Promise<void> {
  return mutateJson<void>(baseUrl, '/auth/token', undefined, locale, 'DELETE');
}

export function logout(baseUrl: string): Promise<void> {
  return mutateJson<void>(baseUrl, '/auth/logout');
}

export async function getPushDevices(baseUrl: string): Promise<PushDevice[]> {
  return (await getJson<{ devices: PushDevice[] }>(baseUrl, '/notifications/devices')).devices;
}

export async function registerPushDevice(baseUrl: string, data: RegisterPushDevice, locale = 'cs'): Promise<PushDevice> {
  return (await mutateJson<{ device: PushDevice }>(baseUrl, '/notifications/devices', data, locale)).device;
}

export function removePushDevice(baseUrl: string, id: number, locale = 'cs'): Promise<void> {
  return mutateJson<void>(baseUrl, `/notifications/devices/${id}`, undefined, locale, 'DELETE');
}

export function removeCurrentPushDevice(baseUrl: string, installationId: string, locale = 'cs'): Promise<void> {
  return mutateJson<void>(baseUrl, '/notifications/devices/current', { installation_id: installationId }, locale, 'DELETE');
}

export async function updatePushDevice(baseUrl: string, id: number, data: { label?: string | null; enabled?: boolean }, locale = 'cs'): Promise<PushDevice> {
  return (await mutateJson<{ device: PushDevice }>(baseUrl, `/notifications/devices/${id}`, data, locale, 'PATCH')).device;
}

export function sendTestPush(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'sent'; message_id: string }> {
  return mutateJson(baseUrl, `/notifications/devices/${id}/test`, undefined, locale);
}

export async function getPushDeviceAreas(baseUrl: string, id: number): Promise<PushArea[]> {
  return (await getJson<{ areas: PushArea[] }>(baseUrl, `/notifications/devices/${id}/areas`)).areas;
}

export function savePushDeviceAreas(baseUrl: string, id: number, areas: PushArea[], locale = 'cs'): Promise<{ areas: PushArea[] }> {
  return mutateJson(baseUrl, `/notifications/devices/${id}/areas`, { areas }, locale, 'PUT');
}

export function getNotificationPreferences(baseUrl: string, aid?: number): Promise<NotificationPreferences> {
  const query = aid === undefined ? '' : `?aid=${encodeURIComponent(aid)}`;
  return getJson<NotificationPreferences>(baseUrl, `/notifications/preferences${query}`);
}

export async function getManagedSenders(baseUrl: string): Promise<ManagedSender[]> {
  return (await getJson<{ data: ManagedSender[] }>(baseUrl, '/settings/notifications/senders')).data;
}

export async function getManagedAlertTypes(baseUrl: string): Promise<ManagedAlertType[]> {
  return (await getJson<{ data: ManagedAlertType[] }>(baseUrl, '/settings/notifications/alert-types')).data;
}

export function createManagedAlertType(baseUrl: string, data: { name: string; description: string }, locale = 'cs'): Promise<{ status: 'created'; data: ManagedAlertType }> {
  return mutateJson(baseUrl, '/settings/notifications/alert-types', data, locale);
}

export function updateManagedAlertType(baseUrl: string, id: number, data: { name: string; description: string }, locale = 'cs'): Promise<{ status: 'updated'; data: ManagedAlertType }> {
  return mutateJson(baseUrl, `/settings/notifications/alert-types/${id}`, data, locale, 'PUT');
}

export function deleteManagedAlertType(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'deleted' }> {
  return mutateJson(baseUrl, `/settings/notifications/alert-types/${id}`, undefined, locale, 'DELETE');
}

export async function getManagedAlertDefinitions(baseUrl: string): Promise<ManagedAlertDefinitionResponse> {
  return getJson(baseUrl, '/settings/notifications/objects');
}

export function createManagedAlertDefinition(baseUrl: string, data: Record<string, unknown>, locale = 'cs'): Promise<{ status: 'created'; data: ManagedAlertDefinition }> {
  return mutateJson(baseUrl, '/settings/notifications/objects', data, locale);
}

export function updateManagedAlertDefinition(baseUrl: string, id: number, data: Record<string, unknown>, locale = 'cs'): Promise<{ status: 'updated'; data: ManagedAlertDefinition }> {
  return mutateJson(baseUrl, `/settings/notifications/objects/${id}`, data, locale, 'PUT');
}

export function getManagedAlertMasks(baseUrl: string, id: number): Promise<ManagedAlertMasks> {
  return getJson(baseUrl, `/settings/notifications/objects/${id}/masks`);
}

export function createManagedAlertMask(baseUrl: string, id: number, data: Record<string, unknown>, locale = 'cs'): Promise<{ status: 'created'; id: number }> {
  return mutateJson(baseUrl, `/settings/notifications/objects/${id}/masks`, data, locale);
}

export function updateManagedAlertMask(baseUrl: string, id: number, maskId: number, data: Record<string, unknown>, locale = 'cs'): Promise<{ status: 'updated'; id: number }> {
  return mutateJson(baseUrl, `/settings/notifications/objects/${id}/masks/${maskId}`, data, locale, 'PUT');
}

export function deleteManagedAlertMask(baseUrl: string, id: number, maskId: number, locale = 'cs'): Promise<{ status: 'deleted'; id: number }> {
  return mutateJson(baseUrl, `/settings/notifications/objects/${id}/masks/${maskId}`, undefined, locale, 'DELETE');
}

export function createManagedSender(baseUrl: string, data: { name: string; description: string; event_address: string; maintenance_address: string }, locale = 'cs'): Promise<{ status: 'created'; data: ManagedSender }> {
  return mutateJson(baseUrl, '/settings/notifications/senders', data, locale);
}

export async function getManagedSender(baseUrl: string, id: number): Promise<ManagedSender> {
  return (await getJson<{ data: ManagedSender }>(baseUrl, `/settings/notifications/senders/${id}`)).data;
}

export function updateManagedSender(baseUrl: string, id: number, data: { event_address: string; maintenance_address: string; debug_payload?: boolean }, locale = 'cs'): Promise<{ status: 'updated'; data: ManagedSender }> {
  return mutateJson(baseUrl, `/settings/notifications/senders/${id}`, data, locale, 'PUT');
}

export function deleteManagedSender(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'deleted' }> {
  return mutateJson(baseUrl, `/settings/notifications/senders/${id}`, undefined, locale, 'DELETE');
}

export async function getManagedSenderEventTypes(baseUrl: string, id: number): Promise<{ data: SenderEventType[]; debug?: SenderDebugEntry[] }> {
  return getJson(baseUrl, `/settings/notifications/senders/${id}/event-types`);
}

export async function getManagedSenderEventLists(baseUrl: string, id: number): Promise<{ data: Record<string, SenderEventStep[] | string> | SenderEventList[]; debug?: SenderDebugEntry[] }> {
  return getJson(baseUrl, `/settings/notifications/senders/${id}/event-lists`);
}

export function getManagedSenderEventList(baseUrl: string, id: number, name: string): Promise<{ data: SenderEventList; debug?: SenderDebugEntry[] }> {
  return getJson(baseUrl, `/settings/notifications/senders/${id}/event-lists/${encodeURIComponent(name)}`);
}

export function saveManagedSenderEventList(baseUrl: string, id: number, name: string, steps: SenderEventStep[], locale = 'cs'): Promise<{ status: 'saved' }> {
  return mutateJson(baseUrl, `/settings/notifications/senders/${id}/event-lists/${encodeURIComponent(name)}`, { steps }, locale, 'PUT');
}

export function deleteManagedSenderEventChain(baseUrl: string, id: number, name: string, chainId: number, locale = 'cs'): Promise<{ status: 'deleted' }> {
  return mutateJson(baseUrl, `/settings/notifications/senders/${id}/event-lists/${encodeURIComponent(name)}`, { chain_id: chainId }, locale, 'DELETE');
}

export function saveNotificationPreferences(
  baseUrl: string,
  data: { schedule: NotificationScheduleEntry[]; defaults: Array<{ aid: number; default_sender_value: boolean }> },
  locale = 'cs',
): Promise<NotificationPreferences> {
  return mutateJson<NotificationPreferences>(baseUrl, '/notifications/preferences', data, locale, 'PUT');
}

export async function getNeoNotifications(baseUrl: string, unreadOnly = false, includeLegacy = false): Promise<NeoNotification[]> {
  const params = new URLSearchParams();
  if (unreadOnly) params.set('unread_only', '1');
  if (includeLegacy) params.set('include_legacy', '1');
  const query = params.toString() ? `?${params.toString()}` : '';
  return (await getJson<{ data: NeoNotification[] }>(baseUrl, `/notifications${query}`)).data;
}

export function markNeoNotificationRead(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'read' }> {
  return mutateJson(baseUrl, `/notifications/${id}/read`, undefined, locale);
}

export function createUserReport(baseUrl: string, data: CreateUserReport, locale = 'cs'): Promise<{ status: 'created'; id: number }> {
  return mutateJson(baseUrl, '/notifications/reports', data, locale);
}

export async function getMyUserReports(baseUrl: string): Promise<MyUserReport[]> {
  return (await getJson<{ data: MyUserReport[] }>(baseUrl, '/notifications/reports')).data;
}

export function temporarilyDisableNotification(baseUrl: string, id: number, data: { minutes?: number; until?: string }, locale = 'cs'): Promise<{ status: 'paused'; aid: number; until: string }> {
  return mutateJson(baseUrl, `/notifications/${id}/temporary-off`, data, locale);
}

export function temporarilyDisableNotificationArea(baseUrl: string, aid: number, data: { minutes?: number; until?: string }, locale = 'cs'): Promise<{ status: 'paused'; aid: number; until: string }> {
  return mutateJson(baseUrl, `/notifications/areas/${aid}/temporary-off`, data, locale);
}

export function enableNotificationArea(baseUrl: string, aid: number, locale = 'cs'): Promise<{ status: 'active'; aid: number }> {
  return mutateJson(baseUrl, `/notifications/areas/${aid}/temporary-off`, undefined, locale, 'DELETE');
}

export function submitFeedback(baseUrl: string, message: string, locale = 'cs'): Promise<{ status: 'sent' }> {
  return mutateJson(baseUrl, '/feedback', { message }, locale);
}

export async function getManagedUsers(baseUrl: string): Promise<ManagedUsersResponse> {
  const response = await getJson<{ data: ManagedUser[]; role_options: ManagedRoleSummary[]; can_change_parent: boolean }>(baseUrl, '/admin/users');
  return {
    users: response.data,
    role_options: response.role_options,
    can_change_parent: response.can_change_parent,
  };
}

export async function getManagedRoles(baseUrl: string): Promise<{ roles: ManagedRole[]; capabilities: string[] }> {
  const response = await getJson<{ data: ManagedRole[]; capabilities: string[] }>(baseUrl, '/admin/roles');
  return { roles: response.data, capabilities: response.capabilities };
}

export function getManagedUserAccess(baseUrl: string, id: number): Promise<ManagedUserAccess> {
  return getJson<ManagedUserAccess>(baseUrl, '/admin/users/' + id + '/access');
}

export function getManagedUserCameras(baseUrl: string, userId: number, neuronId: number): Promise<{ cameras: ManagedUserCameraAccess[]; access_available: boolean }> {
  return getJson(baseUrl, '/admin/users/' + userId + '/access/neurons/' + neuronId + '/cameras');
}

export function updateManagedUserAccess(baseUrl: string, id: number, data: { neuron_ids: number[]; camera_ids: number[] }, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, '/admin/users/' + id + '/access', data, locale, 'PUT');
}

export function getManagedUserNotifications(baseUrl: string, id: number): Promise<{ available: boolean; alerts: ManagedUserNotificationAccess[] }> {
  return getJson(baseUrl, '/admin/users/' + id + '/notifications');
}

export function updateManagedUserNotifications(baseUrl: string, id: number, permissions: Array<{ aid: number; allowed: NotificationPermission }>, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, '/admin/users/' + id + '/notifications', { permissions }, locale, 'PUT');
}

export function createManagedUser(baseUrl: string, data: SaveManagedUser, locale = 'cs'): Promise<{ id: number }> {
  return mutateJson(baseUrl, '/admin/users', data, locale);
}

export function updateManagedUser(baseUrl: string, id: number, data: SaveManagedUser, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/users/${id}`, data, locale, 'PUT');
}

export function getManagedUserDeletionImpact(baseUrl: string, id: number): Promise<ManagedUserDeletionImpact> {
  return getJson(baseUrl, `/admin/users/${id}/deletion-impact`);
}

export function deleteManagedUser(baseUrl: string, id: number, confirmation: string, locale = 'cs'): Promise<{ status: 'deleted'; deleted_user_id: number; reassigned_children: number; preserved_reports: number }> {
  return mutateJson(baseUrl, `/admin/users/${id}`, { confirmation }, locale, 'DELETE');
}

export function createManagedRole(baseUrl: string, data: SaveManagedRole, locale = 'cs'): Promise<{ id: number }> {
  return mutateJson(baseUrl, '/admin/roles', data, locale);
}

export function updateManagedRole(baseUrl: string, id: number, data: SaveManagedRole, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/roles/${id}`, data, locale, 'PUT');
}

export function updateRoleCapabilities(baseUrl: string, id: number, capabilities: string[], locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/roles/${id}/capabilities`, { capabilities }, locale, 'PUT');
}

export async function getInstallations(baseUrl: string): Promise<Installation[]> {
  return (await getJson<{ installations: Installation[] }>(baseUrl, '/trainer/installations')).installations;
}

export async function getNeurons(baseUrl: string): Promise<NeuronServer[]> {
  return (await getJson<{ data: NeuronServer[] }>(baseUrl, '/settings/neurons')).data;
}

export async function getSurveillanceCameras(baseUrl: string): Promise<SurveillanceCamera[]> {
  return (await getJson<{ data: SurveillanceCamera[] }>(baseUrl, '/cameras')).data;
}

export function addSurveillanceCameraFavorite(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; weight: number }> {
  return mutateJson(baseUrl, `/cameras/${id}/favorite`, undefined, locale, 'POST');
}

export function removeSurveillanceCameraFavorite(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/cameras/${id}/favorite`, undefined, locale, 'DELETE');
}

export function reorderSurveillanceCameraFavorites(baseUrl: string, cameraIds: number[], locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, '/cameras/favorites/order', { camera_ids: cameraIds }, locale, 'PUT');
}

export function updateSurveillanceCameraLabel(baseUrl: string, id: number, label: string | null, scope: 'global' | 'user' = 'user', locale = 'cs'): Promise<UpdateSurveillanceCameraLabelResponse> {
  return mutateJson(baseUrl, `/cameras/${id}/label`, { label, scope }, locale, 'PATCH');
}
export function updateSurveillanceRoomLabel(baseUrl: string, id: number, label: string | null, scope: 'global' | 'user' = 'user', locale = 'cs'): Promise<UpdateSurveillanceCameraLabelResponse> {
  return mutateJson(baseUrl, `/cameras/rooms/${id}/label`, { label, scope }, locale, 'PATCH');
}

export function getSurveillanceCameraThumbnailUrl(baseUrl: string, camera: SurveillanceCamera): string {
  return apiUrl(baseUrl, camera.thumbnail_url.replace(/^\/api\/v1/, ''));
}

export function startLegacyCameraLive(baseUrl: string, id: number, locale = 'cs'): Promise<{ camera_id: number; url: string }> {
  return mutateJson(baseUrl, `/cameras/${id}/live`, undefined, locale);
}

export function keepLegacyCameraAlive(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'ok' }> {
  return mutateJson(baseUrl, `/cameras/${id}/keep-alive`, undefined, locale);
}

export async function getLegacyCameraRecords(baseUrl: string, id: number): Promise<CameraRecordInterval[]> {
  return (await getJson<{ data: CameraRecordInterval[] }>(baseUrl, `/cameras/${id}/records`)).data;
}

export async function getLegacyCameraRecordTimeline(baseUrl: string, id: number): Promise<CameraRecordTimeline> {
  const result = await getJson<{ data: CameraRecordInterval[]; alerts?: CameraRecordEvent[] }>(baseUrl, `/cameras/${id}/records`);
  return { intervals: result.data, alerts: result.alerts ?? [] };
}

export async function getLegacyCameraRecordPlaylist(baseUrl: string, id: number, from: string, till: string, locale = 'cs'): Promise<CameraPlaylist> {
  return (await mutateJson<{ playlist: CameraPlaylist }>(baseUrl, `/cameras/${id}/records/playlist`, { from, till }, locale)).playlist;
}

export async function getNeoCameraCodecs(baseUrl: string, id: number): Promise<NeoCodec[]> {
  return (await getJson<{ supported_codecs: NeoCodec[] }>(baseUrl, `/cameras/${id}/neo/codecs`)).supported_codecs;
}

export function getNeoCameraTimeline(baseUrl: string, id: number, from: number, to: number): Promise<NeoCameraTimeline> {
  return getJson(baseUrl, `/cameras/${id}/neo/timeline?from_ts=${Math.floor(from)}&to_ts=${Math.floor(to)}`);
}

export function startNeoCameraLive(baseUrl: string, id: number, supportedCodecs: NeoCodec[], historySeconds = 600, locale = 'cs'): Promise<NeoPlaybackSession> {
  return mutateJson(baseUrl, `/cameras/${id}/neo/playback/live`, { supported_codecs: supportedCodecs, history_seconds: String(historySeconds) }, locale);
}

export function startNeoCameraRange(baseUrl: string, id: number, supportedCodecs: NeoCodec[], from: number, to: number, locale = 'cs'): Promise<NeoPlaybackSession> {
  return mutateJson(baseUrl, `/cameras/${id}/neo/playback/range`, { supported_codecs: supportedCodecs, from_ts: String(Math.floor(from)), to_ts: String(Math.floor(to)) }, locale);
}

export function keepNeoCameraAlive(baseUrl: string, id: number, playbackId: string, locale = 'cs'): Promise<void> {
  return mutateJson(baseUrl, `/cameras/${id}/neo/playback/${playbackId}/keepalive`, undefined, locale);
}

export function closeNeoCameraPlayback(baseUrl: string, id: number, playbackId: string, locale = 'cs'): Promise<void> {
  return mutateJson(baseUrl, `/cameras/${id}/neo/playback/${playbackId}`, undefined, locale, 'DELETE');
}

export async function getLicensePlateLists(baseUrl: string): Promise<LicensePlateListsResponse> {
  return getJson<LicensePlateListsResponse>(baseUrl, '/settings/license-plates');
}
export async function getLicensePlateCars(baseUrl: string, listId: number): Promise<LicensePlateCar[]> {
  return (await getJson<{ data: LicensePlateCar[] }>(baseUrl, '/settings/license-plates/' + listId + '/cars')).data;
}
export function createLicensePlateCar(baseUrl: string, listId: number, data: Omit<LicensePlateCar, 'id'>, locale = 'cs'): Promise<LicensePlateCar> {
  return mutateJson<{ data: LicensePlateCar }>(baseUrl, '/settings/license-plates/' + listId + '/cars', data, locale).then((result) => result.data);
}
export function updateLicensePlateCar(baseUrl: string, listId: number, carId: number, data: Omit<LicensePlateCar, 'id'>, locale = 'cs'): Promise<void> {
  return mutateJson(baseUrl, '/settings/license-plates/' + listId + '/cars/' + carId, data, locale, 'PUT');
}
export function deleteLicensePlateCar(baseUrl: string, listId: number, carId: number, locale = 'cs'): Promise<void> {
  return mutateJson(baseUrl, '/settings/license-plates/' + listId + '/cars/' + carId, undefined, locale, 'DELETE');
}
export type SaveLicensePlateList = { name: string; description?: string | null; max_plates?: number; neuron_ids?: number[] };
export async function createLicensePlateList(baseUrl: string, data: SaveLicensePlateList, locale = 'cs'): Promise<LicensePlateList> {
  return (await mutateJson<{ data: LicensePlateList }>(baseUrl, '/settings/license-plates', data, locale)).data;
}
export async function updateLicensePlateList(baseUrl: string, id: number, data: SaveLicensePlateList, locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, `/settings/license-plates/${id}`, data, locale, 'PUT');
}
export async function updateLicensePlateListNeurons(baseUrl: string, id: number, neuron_ids: number[], locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, `/settings/license-plates/${id}/neurons`, { neuron_ids }, locale, 'PUT');
}
export async function deleteLicensePlateList(baseUrl: string, id: number, locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, `/settings/license-plates/${id}`, undefined, locale, 'DELETE');
}
export async function getLicensePlateDefinitions(baseUrl: string, listId: number): Promise<LicensePlateDefinitionsResponse> {
  return getJson<LicensePlateDefinitionsResponse>(baseUrl, `/settings/license-plates/${listId}/definitions`);
}
export async function saveLicensePlateDefinition(baseUrl: string, listId: number, cameraId: number, sensorId: number, mid: number, locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, `/settings/license-plates/${listId}/definitions`, { camera_id: cameraId, sensor_id: sensorId, mid }, locale, 'PUT');
}
export async function deleteLicensePlateDefinition(baseUrl: string, listId: number, cid: number, locale = 'cs'): Promise<void> {
  await mutateJson(baseUrl, `/settings/license-plates/${listId}/definitions/${cid}`, undefined, locale, 'DELETE');
}
export async function getLicensePlateCameraMasks(baseUrl: string, listId: number, cid: number): Promise<CameraMask[]> {
  return (await getJson<{ data: CameraMask[] }>(baseUrl, `/settings/license-plates/${listId}/definitions/${cid}/masks`)).data;
}
export async function createLicensePlateCameraMask(baseUrl: string, listId: number, cid: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ data: CameraMask }>(baseUrl, `/settings/license-plates/${listId}/definitions/${cid}/masks`, data, locale, 'POST')).data;
}
export async function updateLicensePlateCameraMask(baseUrl: string, listId: number, cid: number, mid: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ data: CameraMask }>(baseUrl, `/settings/license-plates/${listId}/definitions/${cid}/masks/${mid}`, data, locale, 'PUT')).data;
}
export function deleteLicensePlateCameraMask(baseUrl: string, listId: number, cid: number, mid: number, locale = 'cs'): Promise<unknown> {
  return mutateJson(baseUrl, `/settings/license-plates/${listId}/definitions/${cid}/masks/${mid}`, undefined, locale, 'DELETE');
}

export type CreateNeuron = { name: string | null; label: string | null; description: string | null; warranty_until?: string | null; modem_type: string | null; vpn_ip_addr: string | null; ip_addr: string | null; dongle_type: string | null; ups_type: string | null; nas_user: string | null; rsync_bitrate: number | null; nas_server: string | null; nas_folder: string | null; stream_server: string | null; stream_user: string | null; streaming_system: CameraStreamingSystem; local_monitor_enable: number | null; num_days_record: number | null };

export async function createNeuron(baseUrl: string, data: CreateNeuron, locale = 'cs'): Promise<NeuronServer> {
  return (await mutateJson<{ status: string; data: NeuronServer }>(baseUrl, '/settings/neurons', data, locale, 'POST')).data;
}

export type UpdateNeuron = CreateNeuron;

export async function updateNeuron(baseUrl: string, id: number, data: UpdateNeuron, locale = 'cs'): Promise<{ status: string; data: NeuronServer }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}`, data, locale, 'PUT');
}

export async function updateNeuronLabel(baseUrl: string, id: number, label: string | null, scope: 'global' | 'user' = 'user', locale = 'cs'): Promise<UpdateNeuronLabelResponse> {
  return mutateJson(baseUrl, `/settings/neurons/${id}/label`, { label, scope }, locale, 'PATCH');
}

export type NewNuc = { label?: string; name?: string; description?: string; ip_address?: string; res_x: number; res_y: number };
export type NewRoom = { label?: string; name?: string; description?: string };
export type SensorFormValues = {
  label?: string;
  name?: string;
  description?: string;
  type: string;
  bit?: number | null;
  bit_state: number;
  output: number;
  set_cmd?: string;
  clear_cmd?: string;
};
export type CameraFormValues = {
  label?: string; name?: string; description?: string; warranty_until?: string | null; stream_type?: string; audio_type?: string; stream_addr?: string; stream_user?: string; stream_pass?: string;
  stream_latency?: number; high?: number; lens_type?: number;
  res_x?: number; res_y?: number; poe_type?: string; poe_id?: string; poe_chan?: string; cam_enable?: number; footfall_enable?: number;
  http_stream_enable?: number; ss_stream_enable?: number; rec_enable?: number; ls_stream_enable?: number; face_enable?: number; ff_learn_rate?: number;
  adfors_enable?: number; adfors_motor_id?: number; rotate?: string; dnn_type?: string; sub_stream_addr?: string; sub_stream_type?: string; opencl_enable?: number;
};
export type CameraCopyValues = { name: string; ip_address: string };
export type CameraFlag = 'cam_enable' | 'footfall_enable' | 'http_stream_enable' | 'ss_stream_enable' | 'rec_enable' | 'ls_stream_enable';

export async function createNeuronNuc(baseUrl: string, serverId: number, data: NewNuc, locale = 'cs'): Promise<NeuronLocalServer> {
  return (await mutateJson<{ status: string; data: NeuronLocalServer }>(baseUrl, `/settings/neurons/${serverId}/nucs`, data, locale, 'POST')).data;
}

export async function createNeuronRoom(baseUrl: string, serverId: number, data: NewRoom, locale = 'cs'): Promise<NeuronRoom> {
  return (await mutateJson<{ status: string; data: NeuronRoom }>(baseUrl, `/settings/neurons/${serverId}/rooms`, data, locale, 'POST')).data;
}

export async function createNeuronSensor(baseUrl: string, roomId: number, data: SensorFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/rooms/${roomId}/sensors`, data, locale, 'POST')).data;
}

export async function createNeuronCamera(baseUrl: string, roomId: number, data: CameraFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/rooms/${roomId}/cameras`, data, locale, 'POST')).data;
}

export async function copyNeuronCamera(baseUrl: string, id: number, data: CameraCopyValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/cameras/${id}/copy`, data, locale, 'POST')).data;
}

export async function updateNeuronSensor(baseUrl: string, id: number, data: SensorFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/sensors/${id}`, data, locale, 'PUT')).data;
}

export async function updateNeuronCamera(baseUrl: string, id: number, data: CameraFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/cameras/${id}`, data, locale, 'PUT')).data;
}

export async function updateNeuronCameraFlag(baseUrl: string, id: number, flag: CameraFlag, value: number, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/cameras/${id}/flags`, { [flag]: value }, locale, 'PATCH')).data;
}

export function getNeuronCameraHttpStreamUrl(baseUrl: string, id: number): string {
  return apiUrl(baseUrl, `/settings/neurons/cameras/${id}/http-stream`);
}

export async function getCameraMasks(baseUrl: string, id: number): Promise<CameraMask[]> {
  return (await getJson<{ data: CameraMask[] }>(baseUrl, `/settings/neurons/cameras/${id}/masks`)).data;
}

export type SaveCameraMask = Omit<CameraMask, 'id' | 'cid' | 'points' | 'value' | 'description'> & {
  value?: number | null;
  description?: string | null;
  points?: CameraMaskPoint[];
};

export async function createCameraMask(baseUrl: string, id: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ status: string; data: CameraMask }>(baseUrl, `/settings/neurons/cameras/${id}/masks`, data, locale, 'POST')).data;
}

export async function updateCameraMask(baseUrl: string, cameraId: number, id: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ status: string; data: CameraMask }>(baseUrl, `/settings/neurons/cameras/${cameraId}/masks/${id}`, data, locale, 'PUT')).data;
}

export function deleteCameraMask(baseUrl: string, cameraId: number, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/cameras/${cameraId}/masks/${id}`, undefined, locale, 'DELETE');
}

export async function getCameraPoints(baseUrl: string, id: number): Promise<CameraPoint[]> {
  return (await getJson<{ data: CameraPoint[] }>(baseUrl, `/settings/neurons/cameras/${id}/points`)).data;
}

export async function updateCameraPoints(baseUrl: string, id: number, points: CameraPoint[], locale = 'cs'): Promise<CameraPoint[]> {
  return (await mutateJson<{ data: CameraPoint[] }>(baseUrl, `/settings/neurons/cameras/${id}/points`, {
    points: points.map(({ p_type, abs_x, abs_y, abs_z, pix_x, pix_y }) => ({ p_type, abs_x, abs_y, abs_z, pix_x, pix_y })),
  }, locale, 'PUT')).data;
}

export async function updateNeuronRoom(baseUrl: string, id: number, data: NewRoom, locale = 'cs'): Promise<{ status: string; data: NeuronRoom }> {
  return mutateJson(baseUrl, `/settings/neurons/rooms/${id}`, data, locale, 'PUT');
}

export function deleteNeuronRoom(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/rooms/${id}`, undefined, locale, 'DELETE');
}

export function deleteNeuron(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}`, undefined, locale, 'DELETE');
}

export async function getRoomBorder(baseUrl: string, id: number): Promise<RoomBorderPoint[]> {
  return (await getJson<{ data: RoomBorderPoint[] }>(baseUrl, `/settings/neurons/rooms/${id}/border`)).data;
}

export async function updateRoomBorder(baseUrl: string, id: number, points: RoomBorderPoint[], locale = 'cs'): Promise<RoomBorderPoint[]> {
  return (await mutateJson<{ data: RoomBorderPoint[] }>(baseUrl, `/settings/neurons/rooms/${id}/border`, { points: points.map(({ x, y }) => ({ x, y })) }, locale, 'PUT')).data;
}

export function updateNeuronNuc(baseUrl: string, id: number, data: { label?: string; name?: string; description?: string; ip_address?: string; res_x: number; res_y: number }, locale = 'cs'): Promise<{ status: string; data: NeuronLocalServer }> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}`, data, locale, 'PUT');
}

export function deleteNeuronNuc(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}`, undefined, locale, 'DELETE');
}

export async function getNeuronNucTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/nucs/${id}/timing`)).data;
}

export async function updateNeuronNucTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/nucs/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export async function getRoomTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/rooms/${id}/timing`)).data;
}

export async function updateRoomTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/rooms/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export function getNeuronNucCameraLayout(baseUrl: string, id: number): Promise<NeuronCameraLayout> {
  return getJson(baseUrl, `/settings/neurons/nucs/${id}/camera-layout`);
}

export function updateNeuronNucCameraLayout(baseUrl: string, id: number, canvas: { width: number; height: number }, layout: SaveNeuronCameraLayoutItem[], locale = 'cs'): Promise<NeuronCameraLayout> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}/camera-layout`, { canvas, layout }, locale, 'PUT');
}

export function getNeuronCameraVirtual(baseUrl: string, id: number): Promise<NeuronVirtualCamera> {
  return getJson(baseUrl, `/settings/neurons/cameras/${id}/virtual`);
}

export function updateNeuronCameraVirtual(baseUrl: string, id: number, layout: Array<{ servant_cid: number; left: number; top: number; width: number; height: number }>, locale = 'cs'): Promise<NeuronVirtualCamera> {
  return mutateJson(baseUrl, `/settings/neurons/cameras/${id}/virtual`, { layout }, locale, 'PUT');
}

export function getNeuronCameraRecords(baseUrl: string, id: number): Promise<CameraRecords> {
  return getJson<{ data: CameraRecords }>(baseUrl, `/settings/neurons/cameras/${id}/records`).then((result) => result.data);
}

export function updateNeuronCameraRecords(baseUrl: string, id: number, data: SaveCameraRecords, locale = 'cs'): Promise<CameraRecords> {
  return mutateJson<{ data: CameraRecords }>(baseUrl, `/settings/neurons/cameras/${id}/records`, data, locale, 'PUT').then((result) => result.data);
}

export async function getNeuronCameraTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/cameras/${id}/timing`)).data;
}

export async function updateNeuronCameraTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/cameras/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export function updateNeuronPoeMode(baseUrl: string, id: number, poeMode: NeuronPoeMode, locale = 'cs'): Promise<{ status: string; id: number; poe_mode: NeuronPoeMode }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}/poe-mode`, { poe_mode: poeMode }, locale, 'PUT');
}

export function applyNeuronSettings(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number; apply_settings: number }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}/apply-settings`, undefined, locale, 'PUT');
}

export async function getUserReportedErrors(baseUrl: string): Promise<UserReportedError[]> {
  return (await getJson<{ data: UserReportedError[] }>(baseUrl, '/trainer/alerts/user-errors')).data;
}

export async function getUserErrorImages(baseUrl: string, id: number): Promise<ImageData[]> {
  return (await getJson<{ data: ImageData[] }>(baseUrl, `/trainer/alerts/user-errors/${id}/images`)).data;
}

export async function getUserReportDetail(baseUrl: string, id: number): Promise<UserReportDetail> {
  return (await getJson<{ data: UserReportDetail }>(baseUrl, `/trainer/alerts/user-reports/${id}`)).data;
}

export function resolveUserError(
  baseUrl: string,
  id: number,
  locale = 'cs',
): Promise<{ status: string; recipients: string[] }> {
  return mutateJson(baseUrl, `/trainer/alerts/user-errors/${id}/resolve`, undefined, locale);
}

export function resolveNeoUserReport(baseUrl: string, id: number, resolution: 'resolved' | 'not_problem', note?: string, locale = 'cs'): Promise<{ status: string; resolution: string; recipients: string[] }> {
  return mutateJson(baseUrl, `/trainer/alerts/user-reports/${id}/resolve`, { resolution, note }, locale);
}

export async function searchAlerts(
  baseUrl: string,
  params: { cs_id?: number; camera_id?: number; aid?: number; from?: string; to?: string; classes?: number[] },
  locale = 'cs',
): Promise<ImageData[]> {
  return (await mutateJson<{ data: ImageData[] }>(baseUrl, '/trainer/alerts/search', params, locale)).data;
}

export function getAlertImageUrl(baseUrl: string, id: number): string {
  return apiUrl(baseUrl, `/trainer/alerts/image/${id}`);
}

export function submitAlertTraining(
  baseUrl: string,
  payload: AlertTrainingPayload,
  locale = 'cs',
): Promise<{ status: string }> {
  return mutateJson(baseUrl, '/trainer/alerts/train', payload, locale);
}

export async function getSuperCheckSummary(
  baseUrl: string,
  params: { cs_id?: number; from?: string; to?: string },
  locale = 'cs',
): Promise<SuperCheckGroup[]> {
  return (await mutateJson<{ groups: SuperCheckGroup[] }>(baseUrl, '/trainer/alerts/super-check/summary', params, locale)).groups;
}

export async function searchSuperCheck(
  baseUrl: string,
  params: { cs_id?: number; class_id: number; from?: string; to?: string },
  locale = 'cs',
): Promise<ImageData[]> {
  return (await mutateJson<{ data: ImageData[] }>(baseUrl, '/trainer/alerts/super-check/search', params, locale)).data;
}

export function confirmSuperCheck(
  baseUrl: string,
  imageIds: number[],
  locale = 'cs',
): Promise<{ status: string; updated: number }> {
  return mutateJson(baseUrl, '/trainer/alerts/super-check/confirm', { image_ids: imageIds }, locale);
}
